import { Injectable } from '@angular/core';
import { Incident } from 'src/app/api/incidents.service';
import { LinkModes } from '../link-trip/link-trip.component';
import { AssignIncidentModalComponent } from './assign-incident-modal/assign-incident-modal.component';
import { LinkReportModalComponent } from './link-report-modal/link-report-modal.component';
import { LinkTripModalComponent } from './link-trip-modal/link-trip-modal.component';
import { MessageBoxComponent } from './message-box/message-box.component';
import { TripDetail } from 'src/app/api/commonInterfaces';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Router } from '@angular/router';
import { ModalServiceBase } from 'src/app/ui/modalServiceBase';
import { SeverityImpactFormComponent } from '../forms/severity-impact-form/severity-impact-form.component';
import { IncidentSeverityImpactFormData, IncidentSeverityImpact } from 'src/app/api/incident-severity-impact.service';
import { Observable } from 'rxjs';
import { ButtonSeverity } from 'primeng/button';

@Injectable({
  providedIn: 'root'
})
export class IncidentModalService extends ModalServiceBase {


  readonly dialogWidths = {
    breakpoints: { '1199px': '75vw', '575px': '90vw' },
    style: { width: '500px' }
  } as const

  constructor(
    router: Router
  ) {
    super(router)
  }

  /**
    * Shows the unlink incident report modal.  
   *
   *  NOTE: You need the primeng DialogService set in the providers arrray of the component
   * this is called from to pass in as a parameter.
   * 
   * The onClose observable returns a result of type OnLinkValues | undefined
   * @param dialogService Dialog service provided in the calling component
   * @param incidents list of incidents to link the report to
   * @param childIncidentId incident report to link
   * @returns 
   */
  showLinkReportModal(dialogService: DialogService, incidents: Incident[], childIncidentId: number) {

    const ref = this.requireDialogRef(dialogService.open(LinkReportModalComponent, {
      header: "Link Incident Report",
      position: "top",
      modal: true,
      data: {
        incidents: incidents,
        childIncidentId: childIncidentId,
        linkMode: LinkModes.Link
      },
      ...this.dialogWidths
    }))

    this.registerDialog(ref);

    return ref

  }

  /**
    * Shows the unlink incident report modal.  
   *
   *  NOTE: You need the primeng DialogService set in the providers arrray of the component
   * this is called from to pass in as a parameter.
   * 
   * The onClose observable returns a result of type OnLinkValues | undefined
   * @param dialogService Dialog service provided in the calling component
   * @param incidentReports list of reports to unlink
   * @param parentIncidentId the incident to unlink from
   * @returns 
   */
  showUnlinkReportModal(dialogService: DialogService, incidentReports: Incident[], parentIncidentId: number): DynamicDialogRef {

    const ref = this.requireDialogRef(dialogService.open(LinkReportModalComponent, {
      header: "Unlink Incident Report",
      position: "top",
      modal: true,
      data: {
        incidents: incidentReports,
        childIncidentId: parentIncidentId, // parent/child flipped since we're unlinking
        linkMode: LinkModes.Unlink
      },
      ...this.dialogWidths
    }))

    this.registerDialog(ref)
    return ref;
  }

  /**
   * Shows the incident assignment modal.  
   *
   *  NOTE: You need the primeng DialogService set in the providers arrray of the component
   * this is called from to pass in as a parameter.
   * 
   * The onClose observable returns a result of type OnAssignValues | undefined
   * @param dialogService Dialog service provided in the calling component
   * @param incidentId The incident to assign
   * @returns Primeng DialogRef
   */
  showAssignModal(dialogService: DialogService, incidentId: number): DynamicDialogRef {
    const ref = this.requireDialogRef(dialogService.open(AssignIncidentModalComponent, {
      header: "Assign Incident",
      position: "top",
      modal: true,
      data: {
        incidentId: incidentId
      },
      ...this.dialogWidths
    }))

    this.registerDialog(ref)
    return ref


  }

  /**
   * Shows the trip link modal.  
   * NOTE: You need the primeng DialogService set in the providers arrray of the component
   * this is called from to pass in as a parameter.
   * 
   * The onClose observable returns a result of type OnTripLinkValues | undefined
   * @param dialogService Dialog service provided in the calling component
   * @param incidentId The incident to link to
   * @returns Primeng DialogRef
   */
  showTripLinkModal(dialogService: DialogService, incidentId: number): DynamicDialogRef {

    const ref = this.requireDialogRef(dialogService.open(LinkTripModalComponent, {
      header: "Link a Trip",
      position: "top",
      modal: true,
      data: {
        incidentId: incidentId,
        name: "Link Trip",
        linkMode: LinkModes.Link
      },
      ...this.dialogWidths
    }))

    this.registerDialog(ref)
    return ref
  }

  /**
 * Shows the trip unlink modal.  
 * NOTE: You need the primeng DialogService set in the providers arrray of the component
 * this is called from to pass in as a parameter.
 * 
 * The onClose observable returns a result of type OnTripLinkValues | undefined
 * @param dialogService Dialog service provided in the calling component
 * @param incidentId The incident to link to
 * @param trips Filtered list of trips to unlink, if already available
 * @returns Primeng DialogRef
 */
  showUnlinkTripModal(dialogService: DialogService, incidentId: number, trips?: TripDetail[]) {

    const ref = this.requireDialogRef(dialogService.open(LinkTripModalComponent, {
      header: "Unlink Trip",
      position: "top",
      modal: true,
      data: {
        incidentId: incidentId,
        linkMode: LinkModes.Unlink,
        trips: trips
      },
      ...this.dialogWidths
    }))

    this.registerDialog(ref)
    return ref

  }

  showMessageBox(dialogService: DialogService, title: string, message: string, options?: { position?: "center" | "top" | "bottom" | "left" | "right" | "top-left" | "top-right" | "bottom-left" | "bottom-right", confirmButtonText?: string, cancelButtonText?: string, showCopyButton?: boolean, showConfirmButton?: boolean, showCancelButton?: boolean, confirmButtonSeverity?: ButtonSeverity }) {

    const ref = this.requireDialogRef(dialogService.open(MessageBoxComponent, {
      header: title,
      position: options?.position ? options.position : "center",
      modal: true,
      minY: 100,
    data: {
        title: title,
        message: message,
        confirmButtonText: options?.confirmButtonText ? options.confirmButtonText : null,
        cancelButtonText: options?.cancelButtonText ? options.cancelButtonText : null,
        showCopyButton: options?.showCopyButton ? options.showCopyButton : null,
        showCancelButton: options?.showCancelButton != undefined ? options.showCancelButton : null,
        showConfirmButton: options?.showConfirmButton != undefined ? options.showConfirmButton : null,
        confirmButtonSeverity: options?.confirmButtonSeverity ? options.confirmButtonSeverity : "success"
      },
      ...this.dialogWidths
    }))

    this.registerDialog(ref)
    return ref
  }

  /**
   * Shows the severity impact assessment form modal.
   * 
   * NOTE: You need the primeng DialogService set in the providers array of the component
   * this is called from to pass in as a parameter.
   * 
   * The onClose observable returns a result of type IncidentSeverityImpactFormData | null
   * @param dialogService Dialog service provided in the calling component
   * @param workItemId The work item ID for the incident
   * @param severityImpact Optional existing severity impact data to edit
   * @returns Observable<IncidentSeverityImpactFormData | null>
   */
  openSeverityImpactForm(dialogService: DialogService, workItemId: number, severityImpact?: IncidentSeverityImpact | null): Observable<IncidentSeverityImpactFormData | null> {
    const ref = this.requireDialogRef(dialogService.open(SeverityImpactFormComponent, {
     
      data: {
        workItemId: workItemId,
        severityImpact: severityImpact
      },
      closable: true,
      modal: true,
      closeOnEscape: true,
      ...this.dialogWidths
    }));

    this.registerDialog(ref);

    return new Observable(observer => {
      ref.onClose.subscribe((result: IncidentSeverityImpactFormData | null) => {
        observer.next(result);
        observer.complete();
      });
    });
  }

}
